import { zodResolver } from '@hookform/resolvers/zod';
import { api } from '@packages/backend/convex/_generated/api';
import {
  type DeliverySuburb,
  formatCents,
  getUserErrorMessage,
  type RestaurantCvx,
  restaurantForms,
} from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { ActionDropdown } from '@/components/ActionDropdown';
import { PriceInput } from '@/components/PriceInput';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ActionButton } from '@/components/ui+/ActionButton';
import { FormWrapper } from '@/components/ui+/FormWrapper';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { handleSubmitErrors } from '@/utils/handleSubmitErrors';
import { DeliverySuburbForm } from './forms/DeliverySuburbForm';

const formValuesSchema = restaurantForms.deliverySettings;
type FormValues = z.output<typeof formValuesSchema>;

export const Route = createFileRoute('/r/$restaurantId/s/delivery')({
  component: Component,
});

function Component() {
  const convex = useConvex();
  const { restaurantId } = Route.useParams();
  const { data, isError, isPending } = useQuery(api.restaurants.getById, { restaurantId });
  const [deliverySuburbFormState, setDeliverySuburbFormState] = useState<
    DeliverySuburb | 'new' | 'closed'
  >('closed');

  const form = useForm({
    resolver: zodResolver(formValuesSchema),
  });

  const deliveryEnabled = useWatch({ control: form.control, name: 'deliveryEnabled' });

  // Subscribe to changes
  useEffect(() => {
    if (!data) return;
    // Ensure un-saved user changes are kept on Convex sync
    form.reset(
      {
        deliveryEnabled: data.deliveryEnabled,
        deliveryMinOrder: data.deliveryMinOrder,
        deliverySuburbs: data.deliverySuburbs,
      },
      { keepDirty: true, keepDirtyValues: true },
    );
  }, [data]);

  const onValidSubmit = async (formValues: FormValues) => {
    try {
      await convex.mutation(api.restaurants.saveDeliverySettings, {
        formValues,
        restaurantId,
      });
      // The saved values are the new baseline, so nothing is dirty any more
      form.reset(form.getValues());
      toast.success('Saved successfully');
    } catch (error) {
      const description = getUserErrorMessage(error);
      toast.error('Error saving!', { description });
    }
  };

  if (isPending || data === null) return <LoadingIcon />;
  if (isError) return null; // Handled in the parent layout
  const restaurant: RestaurantCvx = data;

  const handleDelete = async (restaurantId: string, deliverySuburbUuid: string) => {
    try {
      await convex.mutation(api.restaurants.deleteDeliverySuburb, {
        deliverySuburbUuid,
        restaurantId,
      });
      toast.success('Deleted successfully');
    } catch (error) {
      const description = getUserErrorMessage(error);
      toast.error('Unable to delete!', { description });
    }
  };

  return (
    <>
      {deliverySuburbFormState !== 'closed' && (
        <DeliverySuburbForm
          editDeliverySuburb={
            deliverySuburbFormState === 'new' ? undefined : deliverySuburbFormState
          }
          onClose={() => setDeliverySuburbFormState('closed')}
          restaurantId={restaurant._id}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Delivery Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <FormWrapper {...form}>
            <form id="form" onSubmit={form.handleSubmit(onValidSubmit, handleSubmitErrors)}>
              <div className="grid gap-4">
                {/* Delivery enabled */}
                <FormField
                  control={form.control}
                  name="deliveryEnabled"
                  render={({ field }) => (
                    <FormItem className="w-1/2 flex flex-row items-center justify-between rounded-lg ">
                      <FormControl>
                        <div className="flex items-center space-x-2">
                          <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                          <FormLabel>Delivery Enabled</FormLabel>
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* Min order */}
                {deliveryEnabled && (
                  <FormField
                    control={form.control}
                    name="deliveryMinOrder"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Delivery Min Order ($)</FormLabel>
                        <FormControl>
                          <PriceInput {...field} className="w-24" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {restaurant.deliverySuburbs.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Suburb</TableHead>
                        <TableHead className="hidden md:table-cell">Postcode</TableHead>
                        <TableHead className="hidden md:table-cell">Fee</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {restaurant.deliverySuburbs.map((ds) => {
                        return (
                          <TableRow key={ds.uuid}>
                            <TableCell className="py-0">{ds.suburbName}</TableCell>
                            <TableCell className="py-0">{ds.suburbPostcode}</TableCell>
                            <TableCell className="py-0">{formatCents(ds.fee, '$')}</TableCell>
                            <TableCell className="py-0">
                              <ActionDropdown
                                onDelete={() => handleDelete(restaurantId, ds.uuid)}
                                onEdit={() => setDeliverySuburbFormState(ds)}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>

              <ActionButton
                className={'mt-4'}
                onClick={(e) => {
                  e.preventDefault(); // Avoids triggering form submission
                  setDeliverySuburbFormState('new');
                }}
                variant={'outline'}
              >
                Add Delivery Suburb
              </ActionButton>
            </form>
          </FormWrapper>
        </CardContent>
        <CardFooter className="border-t">
          <ActionButton
            disabled={form.formState.isSubmitting || !form.formState.isDirty}
            loading={form.formState.isSubmitting}
            type="submit"
            form="form"
          >
            Save
          </ActionButton>
        </CardFooter>
      </Card>
    </>
  );
}
